/**
 * The organization's command center: chat activity, product usage, power
 * users, compliance health and model spend over one window, from the
 * workspace server's `/api/amoreg/command-center`.
 *
 * Same idiom as `state/modelSpend.ts`: a cached atom per (organization,
 * first day, last day) over plain `fetch` with the session cookie. The server decides the
 * figures, the tenancy and who may see them (organization admins); the page
 * only draws the answer. A member who is not an admin reads `admins-only`, and
 * a server without the platform database reads `unavailable`.
 *
 * @module state/commandCenter
 */
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useMemo } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";

export const COMMAND_CENTER_RANGES = ["7d", "30d", "6m"] as const;
export type CommandCenterRange = (typeof COMMAND_CENTER_RANGES)[number];

/** The days a request covers, both inclusive (`YYYY-MM-DD`, UTC). */
export interface CommandCenterDays {
  readonly from: string;
  readonly to: string;
}

const FigureSchema = Schema.Struct({
  value: Schema.Number,
  previous: Schema.Number,
  delta: Schema.NullOr(Schema.Number),
});
export type Figure = typeof FigureSchema.Type;

const SurfaceKeySchema = Schema.Literals([
  "chat",
  "governance",
  "probe",
  "automations",
  "connectors",
  "monitor",
]);
export type SurfaceKey = typeof SurfaceKeySchema.Type;

const SeveritySchema = Schema.Literals(["critical", "high", "medium", "low", "unrated"]);
export type Severity = typeof SeveritySchema.Type;

const SnapshotSchema = Schema.Struct({
  organizationId: Schema.String,
  /** The preset the server was asked for, or "custom" for a window by its days. */
  range: Schema.Literals([...COMMAND_CENTER_RANGES, "custom"]),
  generatedAt: Schema.String,
  window: Schema.Struct({
    since: Schema.String,
    until: Schema.String,
    previousSince: Schema.String,
    granularity: Schema.Literals(["day", "week"]),
  }),
  activity: Schema.Struct({
    activeUsers: FigureSchema,
    messages: FigureSchema,
    daily: Schema.Array(
      Schema.Struct({
        date: Schema.String,
        activeUsers: Schema.Number,
        messages: Schema.Number,
        previousActiveUsers: Schema.Number,
        previousMessages: Schema.Number,
      }),
    ),
  }),
  productUsage: Schema.Struct({
    members: Schema.Number,
    attentionBelow: Schema.Number,
    surfaces: Schema.Array(
      Schema.Struct({
        key: SurfaceKeySchema,
        label: Schema.String,
        users: Schema.Number,
        share: Schema.Number,
        previousUsers: Schema.Number,
        previousShare: Schema.Number,
        needsAttention: Schema.Boolean,
      }),
    ),
  }),
  powerUsers: Schema.Array(
    Schema.Struct({
      userId: Schema.String,
      name: Schema.String,
      activeDays: Schema.Number,
      messages: Schema.Number,
    }),
  ),
  compliance: Schema.Struct({
    openFindings: Schema.Number,
    flow: Schema.Array(
      Schema.Struct({ start: Schema.String, opened: Schema.Number, resolved: Schema.Number }),
    ),
    opened: FigureSchema,
    resolved: FigureSchema,
    severity: Schema.Array(Schema.Struct({ severity: SeveritySchema, open: Schema.Number })),
    meanDaysToResolve: Schema.Struct({
      value: Schema.NullOr(Schema.Number),
      previous: Schema.NullOr(Schema.Number),
      count: Schema.Number,
    }),
    recordUpdates: Schema.Struct({
      applied: Schema.Number,
      skipped: Schema.Number,
      pending: Schema.Number,
    }),
    inspectorRuns: FigureSchema,
  }),
  spend: Schema.NullOr(
    Schema.Struct({
      tokens: FigureSchema,
      costGbp: FigureSchema,
      flow: Schema.Array(
        Schema.Struct({ start: Schema.String, tokens: Schema.Number, costGbp: Schema.Number }),
      ),
    }),
  ),
  actions: Schema.Struct({ openFindings: Schema.Number, pendingRecordUpdates: Schema.Number }),
});
export type CommandCenterSnapshot = typeof SnapshotSchema.Type;

export const decodeCommandCenterSnapshot = Schema.decodeUnknownOption(SnapshotSchema);

export type CommandCenterResult =
  | { readonly kind: "ok"; readonly snapshot: CommandCenterSnapshot }
  /** The session can see the organization but does not administer it. */
  | { readonly kind: "admins-only" }
  /** This server has no platform database, or does not have the route. */
  | { readonly kind: "unavailable" }
  | { readonly kind: "error" };

/** Reads a response into a result; exported for tests. */
export async function commandCenterResultOf(response: Response): Promise<CommandCenterResult> {
  if (response.status === 403) {
    const text = await response.text().catch(() => "");
    return text.trim() === "admins-only" ? { kind: "admins-only" } : { kind: "error" };
  }
  const json = (response.headers.get("content-type") ?? "").includes("application/json");
  if (response.status === 404 || (response.ok && !json)) return { kind: "unavailable" };
  if (!response.ok) return { kind: "error" };
  const body: unknown = await response.json().catch(() => null);
  return Option.match(decodeCommandCenterSnapshot(body), {
    onNone: (): CommandCenterResult => ({ kind: "error" }),
    onSome: (snapshot): CommandCenterResult => ({ kind: "ok", snapshot }),
  });
}

const fetchCommandCenter = (key: string) =>
  Effect.tryPromise(async () => {
    const [organizationId = "", from = "", to = ""] = key.split("|");
    if (organizationId.length === 0) return { kind: "unavailable" } as CommandCenterResult;
    const response = await fetch(
      resolvePrimaryEnvironmentHttpUrl("/api/amoreg/command-center", { organizationId, from, to }),
      { credentials: "include" },
    );
    return commandCenterResultOf(response);
  }).pipe(Effect.orElseSucceed((): CommandCenterResult => ({ kind: "error" })));

const commandCenterRuntime = Atom.runtime(Layer.empty);
const commandCenterAtom = Atom.family((key: string) =>
  commandCenterRuntime
    .atom(fetchCommandCenter(key))
    .pipe(Atom.withLabel(`web-amoreg:command-center:${key}`)),
);

/** The last answer per organization, whatever its period; never shared across organizations. */
const lastAnswerByOrg = new Map<string, CommandCenterResult>();

export interface CommandCenterView {
  /** The latest answer; while a new period loads, the previous one stays. */
  readonly result: CommandCenterResult | null;
  /** True while the shown answer is not yet the one for the chosen period. */
  readonly isRefreshing: boolean;
  readonly refresh: () => void;
}

export function useCommandCenter(
  organizationId: string | null,
  days: CommandCenterDays,
): CommandCenterView {
  const { from, to } = days;
  const atom = useMemo(
    () => commandCenterAtom(`${organizationId ?? ""}|${from}|${to}`),
    [organizationId, from, to],
  );
  const state = useAtomValue(atom);
  const current = Option.getOrNull(AsyncResult.value(state));
  // Refetch keeps the frame: hold the last answer for this organization while
  // a new period loads, instead of flashing an empty page.
  const orgKey = organizationId ?? "";
  // A failed answer is shown while it is current, but never held over another period.
  if (current !== null && current.kind !== "error") lastAnswerByOrg.set(orgKey, current);
  const held = lastAnswerByOrg.get(orgKey) ?? null;
  const refresh = useCallback(() => appAtomRegistry.refresh(atom), [atom]);
  return {
    result: current ?? held,
    isRefreshing: current === null || state.waiting,
    refresh,
  };
}
